import React, { useEffect, useState } from "react";
import { makeRequest } from "../assets/functions_helper";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  DialogContentText,
  Snackbar,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useNavigate } from "react-router-dom";
import { NoSuggestionsTextField } from "../components/textFields";
import { CloseButton, SendButton } from "../components/buttons";
import LogoLogo from "../assets/images/Logo_Horizontal.png";
import LoginGoogle from "./loginGoogle";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../components/customDialog";
import CompletationInfo from "./modules/registration/completationInfo";
import { useForm, FormProvider } from "react-hook-form";
import VerificationCode from "./modules/registration/verificationCode";
import { useUserCountry } from "../context/UserCountryContext";

// --- Importación necesaria para animaciones ---
import { motion } from "framer-motion";

// --- Paleta de Colores Mejorada y Vibrante ---
const COLORS = {
  ACCENT_RED: "#FF4747",
  BACKGROUND_START: "#E0F7FA",
  BACKGROUND_END: "#D1C4E9",
  CONTAINER_BG: "#FFFFFF",
  TEXT_PRIMARY: "#212121",
  TEXT_SECONDARY: "#6A6A6A",
  BLUE_ACTION: "#5A4E99",
  SHADOW: "rgba(90, 78, 153, 0.3)",
};

function Login({ setIsLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [openMoreInfo, setOpenMoreInfo] = useState(false);
  const [dataFromGoogle, setDataFromGoogle] = useState(false);
  const [isCodeVerification, setIscodeVerification] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [countdown, setCountdown] = useState(0);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [dataResendCode, setDataResendCode] = useState(false);
  const navigate = useNavigate();
  const methods = useForm();
  const { setUserCountry } = useUserCountry();

  // Efecto para manejar la cuenta regresiva
  useEffect(() => {
    let interval;
    if (isCountdownActive && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsCountdownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCountdownActive, countdown]);

  const setLocalStarageUserAndNavigate = (response) => {
    localStorage.setItem("token", response.access_token);
    localStorage.setItem("tokenType", response.token_type);
    const userRoles = response.user.roles;
    const userId = response.user.id;

    let minRoleId = 0;
    if (userRoles.length > 0) {
      minRoleId = userRoles.reduce(
        (minId, currentRole) => Math.min(minId, currentRole.id),
        userRoles[0].id
      );
    }

    localStorage.setItem("rol", minRoleId);
    localStorage.setItem("userId", userId);
    localStorage.setItem("name", response.user.name);
    localStorage.setItem("has_billing_account", response.user.has_billing_account);

    // Country-Based Pricing: persist the country object so the pricing flow
    // can read user.country.id without extra API calls.
    // If country is null the user will be prompted to select one on the plans page.
    setUserCountry(response.user.country ?? null);

    setIsLoggedIn(true);
    navigate("/");
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (email === "" || password === "") {
      setAlert(true);
      setAlertMessage("Por favor, completa todos los campos");
      return;
    } else {
      makeRequest(
        "POST",
        "users/login",
        {
          username: email,
          password: password,
        },
        "application/x-www-form-urlencoded",
        null,
        null,
        () => {
          navigate("/login");
        }
      )
        .then((response) => {
          setLocalStarageUserAndNavigate(response);
        })
        .catch((error) => {
          console.error(error);
          if (error.toString().includes("401")) {
            setAlert(true);
            setAlertMessage("Usuario o contraseña incorrectos");
          } else if (error.toString().includes("500")) {
            setAlert(true);
            setAlertMessage("Error en el servidor, intente más tarde");
          } else {
            setAlert(true);
            setAlertMessage("Error desconocido");
          }
        });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleFormSubmit = (data) => {
    if (isCodeVerification) {
      onSubmitVerificationCode(data);
    } else {
      setIscodeVerification(true);
      setDataResendCode(data);
      sentRequestVerificationCode(data);
    }
  };

  // Función para enviar el código de verificación (ahora inicia la cuenta regresiva)
  const sentRequestVerificationCode = (data) => {
    makeRequest(
      "POST",
      "restaurant_admin/send_cellphone_verification",
      {
        cellphone_number: `${data.phoneNumber}`,
        country_id: data.countryId,
      },
      "application/json",
      dataFromGoogle.access_token,
      dataFromGoogle.token_type,
      () => {
        navigate("/login");
      }
    )
      .then(() => {
        // Éxito: iniciar cuenta regresiva de 30 segundos
        setCountdown(30);
        setIsCountdownActive(true);
      })
      .catch((error) => {
        if (error.error_code === "0009") {
          // Código no expirado: obtener tiempo restante del backend
          let waitTime = 30;
          if (error.time_remaining) {
            waitTime = error.time_remaining;
          } else if (error.message) {
            const match = error.message.match(/Wait for (\d+\.?\d*) seconds/);
            if (match) waitTime = parseFloat(match[1]);
          }
          const formattedWaitTime = Math.ceil(waitTime);
          setSnackbarMessage(
            `Por favor espera ${formattedWaitTime} segundos antes de volver a generar un nuevo código`
          );
          setSnackbarSeverity("warning");
          setCountdown(formattedWaitTime);
          setIsCountdownActive(true);
        } else if (error.error_code === "0003") {
          setSnackbarMessage(
            "El número de teléfono ya se encuentra registrado"
          );
          setSnackbarSeverity("error");
        } else {
          setSnackbarMessage(`Error al enviar el código de verificación`);
          setSnackbarSeverity("error");
        }
        setSnackbarOpen(true);
      });
  };

  const sentRequestToUpdatedInfo = (data) => {
    makeRequest(
      "POST",
      "restaurant_admin/update_info",
      {
        cellphone_number: `${data.phoneNumber}`,
        country_id: data.countryId,
      },
      "application/json",
      dataFromGoogle.access_token,
      dataFromGoogle.token_type,
      () => {
        navigate("/login");
      }
    )
      .then(() => {
        // Inyectamos el país seleccionado para que el frontend lo tome como país principal para pagos
        const updatedDataFromGoogle = {
          ...dataFromGoogle,
          user: {
            ...dataFromGoogle.user,
            country: { id: data.countryId },
          },
        };
        setLocalStarageUserAndNavigate(updatedDataFromGoogle);
      })
      .catch(() => {
        setSnackbarMessage(`Ocurrio un error al actualizar su informacion`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const onSubmitVerificationCode = (data) => {
    makeRequest(
      "POST",
      `restaurant_admin/verify_cellphone_code/${data.verificationCode}`,
      {},
      "application/json",
      dataFromGoogle.access_token,
      dataFromGoogle.token_type,
      () => {
        navigate("/login");
      }
    )
      .then(() => {
        sentRequestToUpdatedInfo(data);
      })
      .catch(() => {
        setSnackbarMessage(`Ocurrio un error al verificar el codigo`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const generateTokenEatery = (token) => {
    makeRequest(
      "POST",
      "restaurant_admin/google_authentication",
      token,
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      () => {}
    )
      .then((response) => {
        setDataFromGoogle(response);
        if (response.needs_registration === true) {
          setOpenMoreInfo(true);
        } else {
          setLocalStarageUserAndNavigate(response);
        }
      })
      .catch((error) => {
        console.error("Google auth error:", error);
        // Verificar si el error tiene código 0002 (email ya registrado)
        let errorCode = null;
        let errorMessage = "";

        // Intentar extraer error_code de diferentes estructuras
        if (error.error_code) {
          errorCode = error.error_code;
          errorMessage = error.message || "";
        } else if (error.detail && typeof error.detail === "string") {
          // Podría venir como string con JSON embebido
          try {
            const parsed = JSON.parse(error.detail);
            errorCode = parsed.error_code;
            errorMessage = parsed.message;
          } catch (e) {
            // No es JSON, buscar con regex
            const match = error.detail.match(/'error_code': '(\d+)'/);
            if (match) errorCode = match[1];
            const msgMatch = error.detail.match(/'message': '([^']+)'/);
            if (msgMatch) errorMessage = msgMatch[1];
          }
        } else if (typeof error === "string") {
          try {
            const parsed = JSON.parse(error);
            errorCode = parsed.error_code;
            errorMessage = parsed.message;
          } catch (e) {
            const match = error.match(/'error_code':\s*'([^']+)'/);
            if (match) errorCode = match[1];
            const msgMatch = error.match(/'message':\s*'([^']+)'/);
            if (msgMatch) errorMessage = msgMatch[1];
          }
        }

        if (errorCode === "0002" || errorMessage.includes("Email already registered")) {
          setSnackbarMessage(
            "El correo electrónico ya está registrado. Por favor, inicia sesión con tu cuenta."
          );
          setSnackbarSeverity("warning");
        } else {
          setSnackbarMessage(`Ocurrió un error al intentar autenticarse con Google`);
          setSnackbarSeverity("error");
        }
        setSnackbarOpen(true);
      });
  };

  return (
    <>
      <CustomDialog
        open={openMoreInfo}
        onClose={() => {
          setOpenMoreInfo(!openMoreInfo);
          setIscodeVerification(false);
          // Limpiar la cuenta regresiva al cerrar el modal
          setCountdown(0);
          setIsCountdownActive(false);
        }}
      >
        <CustomDialogTitle>Finaliza Registro</CustomDialogTitle>
        <CustomDialogContent>
          <DialogContentText sx={{ textAlign: "center", mb: 2 }}>
            {isCodeVerification ? (
              <>
                Ingresa el código enviado a tu celular
                {countdown > 0 && (
                  <Box mt={1} sx={{ color: "warning.main" }}>
                    Podrás reenviar el código en: {countdown}s
                  </Box>
                )}
              </>
            ) : (
              "Diligencia todos los campos para finalizar tu registro"
            )}
          </DialogContentText>

          <FormProvider {...methods}>
            {isCodeVerification ? <VerificationCode /> : <CompletationInfo />}
          </FormProvider>
        </CustomDialogContent>

        <CustomDialogActions>
          <CloseButton
            onClick={() => {
              setOpenMoreInfo(!openMoreInfo);
              setIscodeVerification(false);
              setCountdown(0);
              setIsCountdownActive(false);
            }}
            sx={{
              backgroundColor: "#E0E0E0",
              color: "#424242",
              "&:hover": {
                backgroundColor: "#D5D5D5",
              },
            }}
          >
            Cerrar
          </CloseButton>
          <SendButton onClick={methods.handleSubmit(handleFormSubmit)}>
            Enviar
          </SendButton>
          {isCodeVerification && (
            <SendButton
              variant="outlined"
              color="secondary"
              disabled={isCountdownActive}
              onClick={() => {
                methods.clearErrors();
                sentRequestVerificationCode(dataResendCode);
              }}
              sx={{
                position: "relative",
                "&:disabled": {
                  borderColor: "action.disabled",
                  color: "text.disabled",
                },
              }}
            >
              {isCountdownActive ? (
                <>
                  Reenviar en {countdown}s
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                </>
              ) : (
                "Reenviar código"
              )}
            </SendButton>
          )}
        </CustomDialogActions>
      </CustomDialog>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: `linear-gradient(135deg, ${COLORS.BACKGROUND_START} 0%, ${COLORS.BACKGROUND_END} 100%)`,
          animation: "gradient-shift 15s ease infinite",
          "@keyframes gradient-shift": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
          backgroundSize: "200% 200%",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            padding: "40px",
            borderRadius: "20px",
            boxShadow: `0px 10px 30px ${COLORS.SHADOW}`,
            backgroundColor: COLORS.CONTAINER_BG,
            maxWidth: "350px",
            width: "90%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <img
              src={LogoLogo}
              alt="Logo"
              style={{ width: "60.79", height: "57.39" }}
            />
          </Box>

          <Box
            onKeyDown={handleKeyDown}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignContent: "center",
              }}
            >
              <LoginGoogle generateTokenEatery={generateTokenEatery} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
              <Box sx={{ flexGrow: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
              <Typography sx={{ mx: 2, color: '#9e9e9e', fontSize: '0.85rem', fontWeight: 500 }}>
                o
              </Typography>
              <Box sx={{ flexGrow: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            </Box>

            <NoSuggestionsTextField
              id="email-input"
              label="Email"
              type="text"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
            />

            <NoSuggestionsTextField
              id="password-input"
              label="Password"
              type="password"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              sx={{
                marginBottom: "10px",
              }}
            />

            <SendButton
              component={motion.button}
              whileHover={{ scale: 1.05, boxShadow: `0 5px 15px ${COLORS.ACCENT_RED}50` }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              variant="contained"
              sx={{
                backgroundColor: COLORS.ACCENT_RED,
                color: "white",
                fontWeight: "bold",
                padding: "10px 0",
                fontSize: "1rem",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: COLORS.ACCENT_RED,
                  opacity: 0.9,
                },
              }}
              startIcon={<SendIcon />}
            >
              Ingresar
            </SendButton>
          </Box>

          <a
            href="/forgotPassword"
            target="_blank"
            style={{
              color: COLORS.BLUE_ACTION,
              textDecoration: "none",
              cursor: "pointer",
              fontSize: "0.9rem",
              marginTop: "5px",
            }}
          >
            ¿Olvidaste tu contraseña?
          </a>
          <a
            href="/register"
            target="_blank"
            style={{
              color: COLORS.BLUE_ACTION,
              textDecoration: "none",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            ¿Aun no tienes cuenta? Registrate!!
          </a>

          {alert ? (
            <Alert
              severity="error"
              onClose={() => {
                setAlert(false);
              }}
              sx={{ width: "100%" }}
            >
              <AlertTitle>Error</AlertTitle>
              {alertMessage}
            </Alert>
          ) : null}
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Login;